import {
  type ReactNode,
  type PropsWithChildren,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/molecules/toggleGroup";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

export type LiftTabItem<T extends string = string> = {
  value: T;
  label: string;
  content?: ReactNode;
};

export type LiftTabsPlacement = "top" | "bottom" | "left" | "right";

const LIFT_TAB_GROUP_BASE = "gap-0 rounded-none border-0 shadow-none w-fit";

const contentWrapperVariants = cva(
  "relative z-10 min-w-0 border bg-background",
  {
    variants: {
      placement: {
        bottom: "-mb-px border-b border-input",
        top: "-mt-px border-t border-input",
        left: "-ml-px min-h-0 flex-1 border-l border-input",
        right: "-mr-px min-h-0 flex-1 border-r border-input",
      },
    },
    defaultVariants: { placement: "bottom" },
  },
);

const tabItemVariants = cva(
  `
    bg-black/5 text-foreground/50
    hover:bg-black/5 hover:text-foreground/50
    data-[state=off]:border-input
    data-[state=on]:relative data-[state=on]:z-10 data-[state=on]:border
    data-[state=on]:border-input data-[state=on]:bg-background
    data-[state=on]:text-foreground
  `,
  {
    variants: {
      placement: {
        bottom: [
          "rounded-t-none rounded-b-md border-t",
          "first:rounded-tl-none first:rounded-tr-none first:rounded-bl-md last:rounded-tl-none last:rounded-tr-none last:rounded-br-md",
          "data-[state=on]:border-t-0",
        ].join(" "),
        top: [
          "rounded-b-none rounded-t-md border-b",
          "first:rounded-tl-md first:rounded-bl-none first:rounded-br-none last:rounded-tr-md last:rounded-bl-none last:rounded-br-none",
          "data-[state=on]:border-b-0",
        ].join(" "),
        left: [
          "w-full rounded-r-none rounded-t-none rounded-b-none rounded-l-md border-r text-right",
          "first:rounded-tl-md first:rounded-bl-none first:rounded-tr-none first:rounded-br-none last:rounded-bl-md last:rounded-tl-none last:rounded-tr-none last:rounded-br-none",
          "data-[state=on]:border-r-0 data-[state=on]:text-right",
        ].join(" "),
        right: [
          "w-full rounded-l-none rounded-t-none rounded-b-none rounded-r-md border-l text-left",
          "first:rounded-tr-md first:rounded-br-none first:rounded-tl-none first:rounded-bl-none last:rounded-br-md last:rounded-tr-none last:rounded-tl-none last:rounded-bl-none",
          "data-[state=on]:border-l-0 data-[state=on]:text-left",
        ].join(" "),
      },
    },
    defaultVariants: { placement: "bottom" },
  },
);

const tabRowVariants = cva("relative z-20 flex overflow-visible", {
  variants: {
    placement: {
      bottom: "justify-end",
      top: "justify-end",
      left: "shrink-0 flex-col items-end",
      right: "shrink-0 flex-col items-start",
    },
  },
  defaultVariants: { placement: "bottom" },
});

const toggleGroupVariants = cva("flex", {
  variants: {
    placement: {
      bottom: "ml-auto flex-row",
      top: "ml-auto flex-row",
      left: "w-max flex-col items-end",
      right: "w-max flex-col items-start",
    },
  },
  defaultVariants: { placement: "bottom" },
});

const ghostRoundedVariants = cva("", {
  variants: {
    placement: {
      bottom: "rounded-b-md",
      top: "rounded-t-md",
      left: "rounded-l-md",
      right: "rounded-r-md",
    },
  },
  defaultVariants: { placement: "bottom" },
});

export interface LiftTabsGroupProps<
  T extends string,
> extends PropsWithChildren {
  items: LiftTabItem<T>[];
  value: T;
  onValueChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
  placement?: LiftTabsPlacement;
  childrenWrapperStyle?: string[];
}

export const LiftTabsGroup = <T extends string>({
  items,
  value,
  onValueChange,
  ariaLabel,
  className,
  placement = "bottom",
  children,
  childrenWrapperStyle = [],
}: LiftTabsGroupProps<T>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tabRowRef = useRef<HTMLDivElement>(null);
  const tabRefsMapRef = useRef<Record<string, HTMLButtonElement | null>>({});
  const [ghostStylesByValue, setGhostStylesByValue] = useState<
    Record<string, { top: number; left: number; width: number; height: number }>
  >({});
  const [ghostLayerRect, setGhostLayerRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  // Second commit stores rects from ResizeObserver/getBoundingClientRect for ghost highlights.
  // CSS-only alternative would avoid measured state but change lift-tab visuals.
  /* eslint-disable @eslint-react/set-state-in-effect -- ResizeObserver + getBoundingClientRect for lift-tab ghost layer */
  useLayoutEffect(() => {
    const rowEl = tabRowRef.current;
    const wrapperEl = wrapperRef.current;
    if (rowEl == null || wrapperEl == null) {
      setGhostStylesByValue({});
      setGhostLayerRect(null);
      return;
    }
    const updateRects = () => {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const rowRect = rowEl.getBoundingClientRect();
      setGhostLayerRect({
        top: rowRect.top - wrapperRect.top,
        left: rowRect.left - wrapperRect.left,
        width: rowRect.width,
        height: rowRect.height,
      });
      const next: Record<
        string,
        { top: number; left: number; width: number; height: number }
      > = {};
      items.forEach((item) => {
        const tabEl = tabRefsMapRef.current[item.value];
        if (tabEl == null) return;
        const tabRect = tabEl.getBoundingClientRect();
        next[item.value] = {
          top: tabRect.top - rowRect.top,
          left: tabRect.left - rowRect.left,
          width: tabRect.width,
          height: tabRect.height,
        };
      });
      setGhostStylesByValue(next);
    };
    updateRects();
    const observer = new ResizeObserver(updateRects);
    observer.observe(rowEl);
    observer.observe(wrapperEl);
    return () => observer.disconnect();
  }, [items, placement]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  const ghostLayer =
    ghostLayerRect != null ? (
      <div
        aria-hidden
        className="
          pointer-events-none absolute z-0 overflow-visible border-0
          bg-transparent
        "
        style={{
          top: ghostLayerRect.top,
          left: ghostLayerRect.left,
          width: ghostLayerRect.width,
          height: ghostLayerRect.height,
        }}
      >
        {items.map((item) => {
          const style = ghostStylesByValue[item.value];
          if (style == null) return null;
          const isActive = item.value === value;
          return (
            <div
              key={item.value}
              aria-hidden
              className={cn(
                "absolute border-0 bg-transparent shadow-md",
                ghostRoundedVariants({ placement }),
              )}
              style={{
                top: style.top,
                left: style.left,
                width: style.width,
                height: style.height,
                display: isActive ? undefined : "none",
              }}
            />
          );
        })}
      </div>
    ) : null;

  const tabRow = (
    <div
      ref={tabRowRef}
      className={cn(tabRowVariants({ placement }), className)}
    >
      <ToggleGroup
        type="single"
        value={value}
        onValueChange={(v) => v && onValueChange(v as T)}
        variant="outline"
        size="xs"
        className={cn(LIFT_TAB_GROUP_BASE, toggleGroupVariants({ placement }))}
        aria-label={ariaLabel}
      >
        {items.map((item) => (
          <ToggleGroupItem
            key={item.value}
            ref={(el) => {
              tabRefsMapRef.current[item.value] = el;
            }}
            value={item.value}
            aria-label={item.label}
            className={tabItemVariants({ placement })}
          >
            {item.content ?? item.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );

  const contentWithWrapper =
    children != null ? (
      <div
        className={cn(
          contentWrapperVariants({
            placement,
          }),
          ...childrenWrapperStyle,
        )}
      >
        {children}
      </div>
    ) : null;

  if (placement === "top") {
    return (
      <div ref={wrapperRef} className="relative z-0 flex flex-col">
        {ghostLayer}
        {tabRow}
        {contentWithWrapper}
      </div>
    );
  }
  if (placement === "left") {
    return (
      <div
        ref={wrapperRef}
        className="relative z-0 flex h-full min-h-0 flex-row"
      >
        {ghostLayer}
        {tabRow}
        {contentWithWrapper}
      </div>
    );
  }
  if (placement === "right") {
    return (
      <div
        ref={wrapperRef}
        className="relative z-0 flex h-full min-h-0 flex-row"
      >
        {ghostLayer}
        {contentWithWrapper}
        {tabRow}
      </div>
    );
  }
  // bottom (default)
  return (
    <div ref={wrapperRef} className="relative z-0 flex flex-col">
      {ghostLayer}
      {contentWithWrapper}
      {tabRow}
    </div>
  );
};
